import { normalizeWidgetTheme, APIURL, appendFlatpickrBaseStyles, initFlatpickrInShadow, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getEChartsTooltipTheme, getNoDataHTML } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import flatpickr from 'flatpickr';
const DEFAULT_SECTORS = [
    'Healthcare',
    'Energy',
    'Basic Materials',
    'Technology',
    'Communication Services',
    'Consumer Cyclical',
    'Consumer Defensive',
    'Financial Services',
    'Industrials',
    'Real Estate',
    'Utilities',
];

export class TraderverseMarketPETrendSparklines {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: any = null;
    private currentChart: any;
    private selectedMaturities: Set<string> = new Set(['5Y', '10Y']);
    private hiddenSeries: Set<string> = new Set();
    private selectedTimePeriod: string = '1M';
    private customStartDate: string | null = null;
    private customEndDate: string | null = null;
    private widgetResizeObserver: ResizeObserver | null = null;

    private sectorCharts: Record<string, any> = {};
    private sectors = [
        { key: 'technology', label: 'Technology', positive: true },
        { key: 'healthcare', label: 'Healthcare', positive: true }
    ];

    /** DOM-safe id slug; sector names like "Basic Materials" must not use raw spaces in ids/selectors */
    private getSectorChartKey(sector: string): string {
        return sector.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }


    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        const symbols = this.fetchSymbols();
        return {
      headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        };
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        await this.injectChartLibrary();
        this.setupEventListeners();
        await this.setupChart(this.config.symbol!);
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }
    

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
      let dataTheme = 'default-dark';
      
      if (this.config.theme === 'white') {
        themeClass = 'whiteMode';
        dataTheme = 'white';
      } else if (this.config.theme === 'black') {
        themeClass = 'blackMode';
        dataTheme = 'black';
      } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        let cardsHtml = '';
        if (this.currentData && Array.isArray(this.currentData.sectors)) {
            cardsHtml = this.currentData.sectors.map((sector: any) => {
                const key = this.getSectorChartKey(sector.sector);
                const label = sector.sector;
                const currentPE = sector.current_pe != null ? sector.current_pe.toFixed(2) : '-';
                const avgPE = sector.historical_average != null ? sector.historical_average.toFixed(2) : '-';
                const trendChange = sector.trend_change_pct != null ? Math.abs(sector.trend_change_pct).toFixed(2) : '-';
                const trendDirection = sector.trend_direction === 'up' ? 'up' : 'down';
                const trendSign = sector.trend_direction === 'up' ? '+' : '-';
                return `
                <div class="sectorCard">
                    <div class="sectorChart" id="${key}Chart"></div>
                    <div class="topTitleRow">
                        <h3>${label}</h3>
                        <span class="change ${trendDirection}"><svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M7.71429 0V0.888889H10.5369L6.85714 4.70489L5.01729 2.79689C4.93692 2.71357 4.82793 2.66676 4.71429 2.66676C4.60064 2.66676 4.49165 2.71357 4.41129 2.79689L0 7.37156L0.606 8L4.71429 3.73956L6.55414 5.64756C6.63451 5.73088 6.7435 5.77768 6.85714 5.77768C6.97078 5.77768 7.07977 5.73088 7.16014 5.64756L11.1429 1.51733V4.44444H12V0H7.71429Z" fill="currentColor"/> </svg> ${trendSign}${trendChange}% </span>
                    </div>
                    <div class="botRow">
                        <p>${currentPE}</p>
                        <p>AVG ${avgPE}</p>
                    </div>
                </div>
                `;
            }).join('');
        } else {
            cardsHtml = this.sectors.map(s => `
                <div class="sectorCard">
                    <div class="sectorChart" id="${s.key}Chart"></div>
                    <div class="topTitleRow">
                        <h3>${s.label}</h3>
                        <span class="change ${s.positive ? 'up' : 'down'}"><svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M7.71429 0V0.888889H10.5369L6.85714 4.70489L5.01729 2.79689C4.93692 2.71357 4.82793 2.66676 4.71429 2.66676C4.60064 2.66676 4.49165 2.71357 4.41129 2.79689L0 7.37156L0.606 8L4.71429 3.73956L6.55414 5.64756C6.63451 5.73088 6.7435 5.77768 6.85714 5.77768C6.97078 5.77768 7.07977 5.73088 7.16014 5.64756L11.1429 1.51733V4.44444H12V0H7.71429Z" fill="currentColor"/> </svg> ${s.positive ? '+' : '-'}5.3% </span>
                    </div>
                    <div class="botRow">
                        <p>28.8</p>
                        <p>AVG 27.8</p>
                    </div>
                </div>
            `).join('');
        }

        // Filter state (defaults)
        const startDate = this.currentStartDate || '2024-09-01';
        const endDate = this.currentEndDate || '2025-10-20';
        const selectedSectors = this.currentSectors || DEFAULT_SECTORS;
        const exchange = this.currentExchange || 'NASDAQ';
        const enableMA = this.currentEnableMA !== undefined ? this.currentEnableMA : true;
        const maWindow = this.currentMAWindow !== undefined ? this.currentMAWindow : 5;
        const showBaseline = this.currentShowBaseline !== undefined ? this.currentShowBaseline : true;

        const sectorChkOptions = DEFAULT_SECTORS.map(sector => {
            const isChecked = selectedSectors.includes(sector) ? 'checked' : '';
            return `<div class="checkBoxField"> <input class="filter-checkbox sector-filter-checkbox" value="${sector}" type="checkbox" ${isChecked} /> <label>${sector} <i>✓</i></label> </div>`;
        }).join('');

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} P/E Trend Sparklines</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
                <div class="item dateRange">
                    <input type="text" class="date-input" />
                    <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
                </div>
                <div class="item">
                    <div class="legendsCollection">
                        <span class="active" data-tooltip="P/E" style="background-color:#6dd5ff;"></span>
                        ${enableMA ? '<span class="active" data-tooltip="Moving Avg" style="background-color:#facc15;"></span>' : ''}
                    </div>
                </div>
            <span></span>

            <div class="filtersBox sideBar hidden">

                <div class="item checkBoxes">
                    ${sectorChkOptions}
                </div>
                <div class="item">
                    <label>Exchange
                        <select id="pe-exchange">
                            <option value="NASDAQ" ${exchange === 'NASDAQ' ? 'selected' : ''}>NASDAQ</option>
                            <option value="NYSE" ${exchange === 'NYSE' ? 'selected' : ''}>NYSE</option>
                        </select>
                    </label>
                </div>
                <div class="item">
                    <label><input type="checkbox" id="pe-enable-ma" ${enableMA ? 'checked' : ''}/> Moving Avg</label>
                </div>
                <div class="item">
                    <label>MA Window <input type="number" id="pe-ma-window" min="1" max="30" value="${maWindow}" style="width:50px;"></label>
                </div>
                <div class="item">
                    <label><input type="checkbox" id="pe-show-baseline" ${showBaseline ? 'checked' : ''}/> Show Baseline</label>
                </div>
          </div>

            </div>

            <div class="chartDv"${this.currentData && this.currentData.sectors?.length ? 'data-testing-id="sector-pe-trend-chart"' : ''}>
                <div class="peTrendSectorMain">
                ${cardsHtml}
                </div>
            </div>

          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
        const filtersBoxEl = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        if (filtersToggle && filtersBoxEl) {
            filtersToggle.addEventListener('click', (ev: Event) => {
                ev.stopPropagation();
                filtersBoxEl.classList.toggle('hidden');
            });
        }
        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) {
                placeholder.replaceWith(dropdown);
            }
        }

        // Add filter events for instant update
        const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
        let defaultDates: string[] | undefined = undefined;
        if (this.currentStartDate && this.currentEndDate) {
            defaultDates = [this.currentStartDate, this.currentEndDate];
            dateInput.value = `${this.currentStartDate} to ${this.currentEndDate}`;
        } else {
            defaultDates = [startDate, endDate];
            dateInput.value = `${startDate} to ${endDate}`;
            this.currentStartDate = startDate;
            this.currentEndDate = endDate;
        }
        initFlatpickrInShadow(dateInput, this.shadowRoot, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            defaultDate: defaultDates,
            onClose: (selectedDates: Date[]) => {
                if (selectedDates.length === 2) {
                    const [start, end] = selectedDates;
                    this.currentStartDate = start.toISOString().slice(0, 10);
                    this.currentEndDate = end.toISOString().slice(0, 10);
                    dateInput.value = `${this.currentStartDate} to ${this.currentEndDate}`;
                    this.setupChart(this.config.symbol!);
                }
            },
        });

        const sectorCheckboxes = this.shadowRoot.querySelectorAll('.sector-filter-checkbox');
        const exchangeSelect = this.shadowRoot.getElementById('pe-exchange') as HTMLSelectElement;
        const enableMAInput = this.shadowRoot.getElementById('pe-enable-ma') as HTMLInputElement;
        const maWindowInput = this.shadowRoot.getElementById('pe-ma-window') as HTMLInputElement;
        const showBaselineInput = this.shadowRoot.getElementById('pe-show-baseline') as HTMLInputElement;

        sectorCheckboxes.forEach(checkbox => {
            checkbox.addEventListener('change', () => {
                const selected: string[] = [];
                this.shadowRoot.querySelectorAll('.sector-filter-checkbox').forEach((cb) => {
                    const input = cb as HTMLInputElement;
                    if (input.checked) selected.push(input.value);
                });
                this.currentSectors = selected;
                this.setupChart(this.config.symbol!);
            });
        });
        if (exchangeSelect) exchangeSelect.addEventListener('change', () => {
            this.currentExchange = exchangeSelect.value;
            this.setupChart(this.config.symbol!);
        });
        if (enableMAInput) enableMAInput.addEventListener('change', () => {
            this.currentEnableMA = enableMAInput.checked;
            this.setupChart(this.config.symbol!);
        });
        if (maWindowInput) maWindowInput.addEventListener('change', () => {
            this.currentMAWindow = Number(maWindowInput.value);
            this.setupChart(this.config.symbol!);
        });
        if (showBaselineInput) showBaselineInput.addEventListener('change', () => {
            this.currentShowBaseline = showBaselineInput.checked;
            this.setupChart(this.config.symbol!);
        });
    }

    

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private async attachStyles(): Promise<void> {
        try {
            appendFlatpickrBaseStyles(this.shadowRoot);
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
        const legendSpans = Array.from(this.shadowRoot.querySelectorAll('.toggle-series')) as HTMLElement[];
        legendSpans.forEach(span => {
            span.addEventListener('click', (e) => {
                const target = e.currentTarget as HTMLElement;
                const maturity = target.getAttribute('data-maturity') || '';
                if (!maturity) return;
                const isActiveNow = target.classList.toggle('active');

                if (isActiveNow) {
                    this.selectedMaturities.add(maturity);
                    this.hiddenSeries.delete(maturity);
                } else {
                    this.selectedMaturities.delete(maturity);
                    this.hiddenSeries.add(maturity);
                }

                this.setupChart(this.config.symbol!);
            });
        });

        const periodSelect = this.shadowRoot.getElementById('timeperiods') as HTMLSelectElement | null;
        if (periodSelect) {
            periodSelect.value = this.selectedTimePeriod;
            periodSelect.addEventListener('change', () => {
                const period = periodSelect.value;
                this.selectedTimePeriod = period;
                this.customStartDate = null;
                this.customEndDate = null;
                const sd = this.shadowRoot.getElementById('start-date') as HTMLInputElement | null;
                const ed = this.shadowRoot.getElementById('end-date') as HTMLInputElement | null;
                if (sd) sd.value = '';
                if (ed) ed.value = '';
                this.setupChart(this.config.symbol!);
            });
        }

        const startInput = this.shadowRoot.getElementById('start-date') as HTMLInputElement | null;
        const endInput = this.shadowRoot.getElementById('end-date') as HTMLInputElement | null;
        const applyCustomRange = () => {
            if (!startInput || !endInput) return;
            const s = startInput.value;
            const e = endInput.value;
            if (s && e) {
                this.customStartDate = s;
                this.customEndDate = e;
                // clear period select when custom range applied
                const ps = this.shadowRoot.getElementById('timeperiods') as HTMLSelectElement | null;
                if (ps) ps.value = '';
                this.setupChart(this.config.symbol!);
            } else if (!s && !e) {
                this.customStartDate = null;
                this.customEndDate = null;
                this.selectedTimePeriod = '1M';
                const ps = this.shadowRoot.getElementById('timeperiods') as HTMLSelectElement | null;
                if (ps) ps.value = this.selectedTimePeriod;
                this.setupChart(this.config.symbol!);
            }
        };
        startInput?.addEventListener('change', applyCustomRange);
        endInput?.addEventListener('change', applyCustomRange);
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

 private async setupChart(symbol: string): Promise<void> {
        try {
            // Use filter state or defaults
            const startDate = this.currentStartDate || '2024-09-01';
            const endDate = this.currentEndDate || '2025-10-20';
            const sectors = this.currentSectors || DEFAULT_SECTORS;
            const exchange = this.currentExchange || 'NASDAQ';
            const enableMA = this.currentEnableMA !== undefined ? this.currentEnableMA : true;
            const maWindow = this.currentMAWindow !== undefined ? this.currentMAWindow : 5;
            const showBaseline = this.currentShowBaseline !== undefined ? this.currentShowBaseline : true;

            // Build query string
            const params = [
                `p_start_date=${encodeURIComponent(startDate)}`,
                `p_end_date=${encodeURIComponent(endDate)}`,
                `p_sectors=${sectors.map((s: string) => encodeURIComponent(s)).join(',')}`,
                `p_exchange_filter=${encodeURIComponent(exchange)}`,
                `p_enable_moving_average=${enableMA}`,
                `p_ma_window=${maWindow}`,
                `p_show_baseline=${showBaseline}`
            ].join('&');

            const url = `${APIURL}/v1/discover/trend-sparklines-sector?${params}`;
            const res = await apiGet({
                method: 'GET',
                url,
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!res.ok) throw new Error(`API error: ${res.status}`);
            const json = await res.json();
            this.currentData = json.data;
            await this.createWidget();
            this.updateDisplay();
            const widgetBox = this.shadowRoot.querySelector('.widgetBox');
            if (widgetBox) {
                widgetBox.classList.remove('loading');
            }
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
            const widgetBox = this.shadowRoot.querySelector('.widgetBox');
            if (widgetBox) {
                widgetBox.classList.remove('loading');
            }
        }
    }
// Add filter state properties
private currentStartDate?: string;
private currentEndDate?: string;
private currentSectors?: string[];
private currentExchange?: string;
private currentEnableMA?: boolean;
private currentMAWindow?: number;
private currentShowBaseline?: boolean;



    private updateDisplay(): void {
        if (!this.currentData || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }

        if (!this.currentData?.sectors?.length) {
            const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
            if (chartDv) {
                chartDv.innerHTML = getNoDataHTML();
            }
            Object.values(this.sectorCharts).forEach((chart) => {
                try { chart?.dispose(); } catch (e) { /* ignore */ }
            });
            this.sectorCharts = {};
            return;
        }

        this.renderAnalystRating();


        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
            this.drawChart();
            });
        });
    }

    private renderAnalystRating(): void {
        this.drawChart();
    }
    formatDateToMMMDD(dateString: string): string {
        const date = new Date(dateString);
        const options = { month: 'short', day: 'numeric' } as const;
        return date.toLocaleDateString('en-US', options);
    }


private drawChart(): void {
    const echarts = (window as any).echarts;
    if (!this.currentData?.sectors || !this.currentData.sectors.length) return;

    const enableMA = this.currentEnableMA !== undefined ? this.currentEnableMA : true;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

    this.currentData.sectors.forEach((sectorData: any) => {
        const sectorKey = this.getSectorChartKey(sectorData.sector);
        const el = this.shadowRoot.getElementById(`${sectorKey}Chart`) as HTMLElement | null;
        if (!el || el.clientHeight === 0) return;

        if (this.sectorCharts[sectorKey]) {
            this.sectorCharts[sectorKey].dispose();
        }

        const chart = echarts.init(el);
        this.sectorCharts[sectorKey] = chart;

        const dates = sectorData.data_points.map((dp: any) => this.formatDateToMMMDD(dp.date));
        const mainSeries = sectorData.data_points.map((dp: any) => dp.pe);
        const avgSeries = sectorData.data_points.map((dp: any) => dp.moving_average);
        const hasMA = enableMA && avgSeries.some((v: any) => v != null);

        const series: any[] = [
            {
                name: 'P/E',
                type: 'line',
                data: mainSeries,
                smooth: true,
                showSymbol: false,
                lineStyle: { color: '#6dd5ff', width: 3 },
                itemStyle: { color: '#6dd5ff' }
            }
        ];

        if (hasMA) {
            series.push({
                name: 'Moving Avg',
                type: 'line',
                data: avgSeries,
                smooth: true,
                showSymbol: false,
                lineStyle: {
                    color: '#facc15',
                    width: 2,
                    type: 'dashed'
                },
                itemStyle: { color: '#facc15' }
            });
        }

        chart.setOption({
            grid: { left: 0, right: 0, top: 4, bottom: 4 },
            xAxis: { type: 'category', show: false, data: dates },
            yAxis: { type: 'value', show: false },
            tooltip: {
                ...getEChartsTooltipTheme(widgetBox),
                show: true,
                trigger: 'axis',
                confine: true,
                formatter: (params: any) => {
                    const rows = Array.isArray(params) ? params : [params];
                    if (!rows.length) return '';
                    const date = rows[0].axisValueLabel ?? rows[0].name ?? '';
                    const lines = rows
                        .filter((p: any) => p.value != null && p.value !== '')
                        .map((p: any) => {
                            const val = typeof p.value === 'number' ? p.value.toFixed(2) : p.value;
                            return `<div style="display:flex;align-items:center;gap:6px;margin:2px 0">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color}"></span>
                                <span>${p.seriesName}: ${val}</span>
                            </div>`;
                        })
                        .join('');
                    return `<div style="font-weight:600;margin-bottom:4px">${date}</div>${lines}`;
                }
            },
            animation: true,
            series
        });

        setTimeout(() => chart.resize(), 0);
    });
}

      
      

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any, stockInfo: any) => this.setupChart(stockId,), (type: any, data: any) => this.emit(type, data));
    }
    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }
  
    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
      if (!this.shadowRoot) return;
    
      this.config.theme = toggleWidgetTheme(
        this.shadowRoot,
        theme
      ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    
      this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseMarketPETrendSparklines: typeof TraderverseMarketPETrendSparklines; } }
if (typeof window !== 'undefined') { window.TraderverseMarketPETrendSparklines = TraderverseMarketPETrendSparklines; }
